import React, { useState, useEffect } from 'react';
import { IDSButton, IDSInput, IDSCard } from '@inera/ids-react';
import { saveQuestionComment, deleteQuestionComment, updateQuestionComment } from '../../services/testExecutionAPI';
import CommentAvatar from './CommentAvatar';
import ConfirmDialog from '../ConfirmDialog';
import { formatDateTimeNoSeconds } from '../../utils/dateTime';

const roleLabel = (role) => (role === 'TESTER' ? 'Testare' : 'Granskare');

/**
 * Comments on a single self-declaration question within a review (test execution).
 * Displayed as message bubbles (IDSCard) that can be collapsed/expanded.
 */
const QuestionCommentThread = ({ executionId, questionId, comments = [], canAdd = false, onAdded, onDeleted }) => {
  const [localComments, setLocalComments] = useState(comments);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showField, setShowField] = useState(false);
  const [open, setOpen] = useState(true);
  // Comment pending delete confirmation.
  const [confirmDeleteComment, setConfirmDeleteComment] = useState(null);
  // Comment currently being edited, and its draft text.
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [rowError, setRowError] = useState(null);

  // Comments are fetched asynchronously by the page, so re-sync when they arrive
  // (otherwise the reviewer's comments never appear for the tester, and vice versa).
  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  const handleAdd = async () => {
    if (!newComment.trim()) return;
    try {
      setLoading(true);
      setError(null);
      const saved = await saveQuestionComment(executionId, questionId, newComment.trim());
      setLocalComments(prev => [...prev, saved]);
      if (typeof onAdded === 'function') onAdded(saved);
      setNewComment('');
      setShowField(false);
    } catch (err) {
      console.error('Error adding question comment:', err);
      setError('Kunde inte spara kommentar');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (comment) => {
    setConfirmDeleteComment(null);
    try {
      await deleteQuestionComment(comment.id);
      setLocalComments(prev => prev.filter(c => c.id !== comment.id));
      if (typeof onDeleted === 'function') onDeleted(comment.id);
    } catch (err) {
      console.error('Error deleting question comment:', err);
      setError('Kunde inte ta bort kommentar');
    }
  };

  const startEdit = (c) => {
    setRowError(null);
    setEditingId(c.id);
    setEditText(c.commentText);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setRowError(null);
  };

  const saveEdit = async (commentId) => {
    if (!editText.trim()) return;
    try {
      setRowError(null);
      const saved = await updateQuestionComment(commentId, editText.trim());
      setLocalComments(prev => prev.map(c => (c.id === commentId ? saved : c)));
      cancelEdit();
    } catch (err) {
      console.error('Error updating question comment:', err);
      setRowError('Kunde inte spara ändringen');
    }
  };

  const formatDate = (d) => formatDateTimeNoSeconds(d);

  const hasComments = localComments.length > 0;

  // Only the comment's own author may edit/remove it, and only while it's still
  // eligible (backend-computed: author's active phase + still in the open round).
  const canEdit = (c) => canAdd && c.mine && c.stillEditable;

  return (
    <div className="ids-mt-2">
      {confirmDeleteComment && (
        <ConfirmDialog
          message="Vill du ta bort kommentaren?"
          onConfirm={() => handleDelete(confirmDeleteComment)}
          onCancel={() => setConfirmDeleteComment(null)}
        />
      )}
      {hasComments && (
        <>
          <IDSButton
            size="s"
            tertiary
            icon={open ? 'chevron-down' : 'chevron-right'}
            onClick={() => setOpen(v => !v)}
            aria-expanded={open}
            aria-label={open ? 'Dölj kommentarer' : 'Visa kommentarer'}
          >
            {`Kommentarer (${localComments.length})`}
          </IDSButton>

          {open && (
            <div className="ids-mt-2 ids-mb-2">
              {localComments.map((c, idx) => {
                const isTester = c.authorRole === 'TESTER';
                const isEditing = editingId === c.id;
                return (
                  <div
                    key={c.id || idx}
                    className="ids-flex ids-mb-4"
                    style={{
                      flexDirection: 'column',
                      alignItems: isTester ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <IDSCard className="ids-text-max-width">
                      <CommentAvatar
                        user={isTester ? 1 : 2}
                        label={c.authorName || roleLabel(c.authorRole)}
                        subtitle={c.authorRole ? roleLabel(c.authorRole) : undefined}
                      />
                      {isEditing ? (
                        <div style={{ minWidth: 260 }}>
                          <IDSInput
                            label="Redigera kommentar"
                            type="text"
                            maxLength={400}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                          />
                          <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <IDSButton size="s" onClick={() => saveEdit(c.id)} disabled={!editText.trim()}>Spara</IDSButton>
                            <IDSButton size="s" secondary onClick={cancelEdit}>Avbryt</IDSButton>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{c.commentText}</div>
                      )}
                    </IDSCard>
                    <div
                      className="ids-small ids-mt-1"
                      style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}
                    >
                      <span>{formatDate(c.createdDate)}</span>
                      {!isEditing && canEdit(c) && (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(c)}
                            title="Redigera kommentar"
                            aria-label="Redigera kommentar"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ids-palette-neutral-40)', padding: 0, font: 'inherit' }}
                          >
                            Redigera
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDeleteComment(c)}
                            title="Ta bort kommentar"
                            aria-label="Ta bort kommentar"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ids-palette-error-40, #c73c30)', padding: 0, font: 'inherit' }}
                          >
                            Ta bort
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
              {rowError && (
                <div className="ids-mt-2">
                  <span style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.85rem' }}>{rowError}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {canAdd && (
        !showField ? (
          <IDSButton size="s" secondary onClick={() => setShowField(true)}>
            + Kommentar
          </IDSButton>
        ) : (
          <div style={{ maxWidth: 420 }}>
            <IDSInput
              label="Lägg till kommentar"
              type="text"
              maxLength={400}
              placeholder="Skriv en kommentar..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
            />
            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <IDSButton size="s" onClick={handleAdd} disabled={loading || !newComment.trim()}>Lägg till</IDSButton>
              <IDSButton size="s" secondary onClick={() => { setShowField(false); setError(null); }}>Avbryt</IDSButton>
              {error && <span style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.85rem' }}>{error}</span>}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default QuestionCommentThread;
